/**
 * API client for the Book4 backend.
 * Uses @book4/config for base URL; token storage is injectable (browser default or mobile AsyncStorage).
 */

import { getApiBaseUrl } from "@book4/config";
import type { AuthUser, CustomerProfile, UpdateBusinessSettingsPayload } from "@book4/types";
import { browserTokenStorage, SWITCH_CUSTOMER_TOKEN_KEY } from "./storage";
import type { TokenStorage } from "./storage";

function decodeJwtPayload(token: string): { role?: string } | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    return payload;
  } catch {
    return null;
  }
}

export function isCustomerToken(token: string): boolean {
  const payload = decodeJwtPayload(token);
  return payload?.role === "CUSTOMER";
}

export function getJwtRole(token: string): string | null {
  const payload = decodeJwtPayload(token);
  return payload?.role ?? null;
}

/** Resolve storage: explicit, or browser default when available */
function resolveStorage(storage?: TokenStorage | null): TokenStorage | null {
  if (storage) return storage;
  if (typeof window !== "undefined") return browserTokenStorage;
  return null;
}

const CUSTOMER_TOKEN_KEY = "book4_customer_token";

export function getApiUrl(): string {
  return getApiBaseUrl();
}

/** Get business JWT – uses storage or Next.js cookies when on server (caller must pass token in that case). */
export async function getToken(storage?: TokenStorage | null): Promise<string | null> {
  const s = resolveStorage(storage);
  if (!s) return null;
  return s.getItem("token");
}

/** Get customer (booking) JWT. In browser, syncs switch token from sessionStorage to localStorage when present. */
export async function getCustomerToken(storage?: TokenStorage | null): Promise<string | null> {
  const s = resolveStorage(storage);
  if (!s) return null;
  let token = await s.getItem(CUSTOMER_TOKEN_KEY);
  if (token && isCustomerToken(token) && typeof sessionStorage !== "undefined") {
    const fromSwitch = sessionStorage.getItem(SWITCH_CUSTOMER_TOKEN_KEY);
    if (fromSwitch === token) {
      await s.setItem(CUSTOMER_TOKEN_KEY, token);
      sessionStorage.removeItem(SWITCH_CUSTOMER_TOKEN_KEY);
    }
  }
  return token;
}

export async function setCustomerToken(
  token: string,
  storage?: TokenStorage | null
): Promise<void> {
  const s = resolveStorage(storage);
  if (!s) return;
  await s.setItem(CUSTOMER_TOKEN_KEY, token);
}

export async function clearCustomerToken(storage?: TokenStorage | null): Promise<void> {
  const s = resolveStorage(storage);
  if (!s) return;
  await s.removeItem(CUSTOMER_TOKEN_KEY);
}

export async function setToken(token: string, storage?: TokenStorage | null): Promise<void> {
  const s = resolveStorage(storage);
  if (!s) return;
  await s.setItem("token", token);
}

export async function clearToken(storage?: TokenStorage | null): Promise<void> {
  const s = resolveStorage(storage);
  if (!s) return;
  await s.removeItem("token");
}

export { SWITCH_CUSTOMER_TOKEN_KEY };

export async function fetchApi<T>(
  path: string,
  options: RequestInit & { token?: string | null } = {}
): Promise<T> {
  const { token, ...init } = options;
  const url = `${getApiUrl()}${path.startsWith("/") ? path : `/${path}`}`;
  const headers = new Headers(init.headers);
  headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);
  let res: Response;
  try {
    res = await fetch(url, { ...init, headers, credentials: "include" });
  } catch {
    throw new Error("The server is temporarily unavailable. Please try again in a moment.");
  }
  if (!res.ok) {
    if (res.status === 502 || res.status === 503 || res.status === 504) {
      throw new Error("The server is temporarily unavailable. Please try again in a moment.");
    }
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? "Request failed");
  }
  return res.json() as Promise<T>;
}

export async function getCurrentCustomer(
  storage?: TokenStorage | null
): Promise<CustomerProfile | null> {
  const token = await getCustomerToken(storage);
  if (!token) return null;
  try {
    const profile = await fetchApi<CustomerProfile>("/api/auth/me", { token });
    return profile?.role === "CUSTOMER" ? profile : null;
  } catch {
    return null;
  }
}

export async function getCurrentUser(storage?: TokenStorage | null): Promise<AuthUser | null> {
  const token = await getToken(storage);
  if (!token) return null;
  try {
    return await fetchApi<AuthUser>("/api/auth/me", {
      token,
      cache: "no-store" as RequestCache,
    });
  } catch {
    return null;
  }
}

/**
 * Result of asking for the linked account's token. refusedMessage is set when the API refused the
 * switch for a reason the person can act on (an email not verified, a link not confirmed, or too
 * many attempts), so the app can say why and how to fix it instead of sending them to a login page
 * without explanation. code says which, so the app can offer the fix (confirm the link, resend the
 * verification email, or wait).
 */
export type SwitchRefusalCode = "EMAIL_NOT_VERIFIED" | "LINK_NOT_CONFIRMED" | "RATE_LIMITED" | "ACCOUNT_LOCKED";
export type LinkedTokenResult =
  | { token: string; refusedMessage?: undefined; code?: undefined }
  | { token: null; refusedMessage: string | null; code?: SwitchRefusalCode; retryAfterSeconds?: number };

const SWITCH_REFUSAL_CODES = new Set(["EMAIL_NOT_VERIFIED", "LINK_NOT_CONFIRMED"]);

/** Seconds from a Retry-After header (or a retryAfterSeconds body field), if there is one. */
function retryAfterSeconds(res: Response, body: { retryAfterSeconds?: number } | null): number | undefined {
  const header = Number(res.headers.get("Retry-After"));
  if (Number.isFinite(header) && header > 0) return Math.ceil(header);
  if (typeof body?.retryAfterSeconds === "number" && body.retryAfterSeconds > 0) return Math.ceil(body.retryAfterSeconds);
  return undefined;
}

export const TOO_MANY_ATTEMPTS_MESSAGE = "Too many attempts. Please wait a few minutes and try again.";

export async function fetchLinkedToken(
  path: "/api/auth/link/customer-token" | "/api/auth/link/business-token",
  token: string
): Promise<LinkedTokenResult> {
  let res: Response;
  try {
    res = await fetch(`${getApiUrl()}${path}`, {
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      credentials: "include",
    });
  } catch {
    return { token: null, refusedMessage: null };
  }
  const body = (await res.json().catch(() => null)) as
    | { token?: string; error?: string; code?: string; retryAfterSeconds?: number }
    | null;
  if (res.ok && body?.token) return { token: body.token };
  if (res.status === 429) {
    const wait = retryAfterSeconds(res, body);
    return {
      token: null,
      refusedMessage: body?.error ?? TOO_MANY_ATTEMPTS_MESSAGE,
      code: body?.code === "ACCOUNT_LOCKED" ? "ACCOUNT_LOCKED" : "RATE_LIMITED",
      ...(wait ? { retryAfterSeconds: wait } : {}),
    };
  }
  if (body?.code && SWITCH_REFUSAL_CODES.has(body.code) && body.error) {
    return { token: null, refusedMessage: body.error, code: body.code as SwitchRefusalCode };
  }
  return { token: null, refusedMessage: null };
}

/**
 * POST /api/auth/link/confirm: link this session's account with the other side by entering the other
 * side's password. canSwitch is false when the link is made but an email still needs verifying.
 */
export type ConfirmAccountLinkResult =
  | { ok: true; canSwitch: boolean }
  | { ok: false; status: number; error: string; code?: string; retryAfterSeconds?: number };

export async function confirmAccountLink(password: string, token: string): Promise<ConfirmAccountLinkResult> {
  let res: Response;
  try {
    res = await fetch(`${getApiUrl()}/api/auth/link/confirm`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      credentials: "include",
      body: JSON.stringify({ password }),
    });
  } catch {
    return { ok: false, status: 0, error: "We could not reach Book4. Check your connection and try again." };
  }
  const body = (await res.json().catch(() => null)) as
    | { success?: boolean; canSwitch?: boolean; error?: string; code?: string; retryAfterSeconds?: number }
    | null;
  if (res.ok && body?.success) return { ok: true, canSwitch: body.canSwitch === true };
  const wait = res.status === 429 ? retryAfterSeconds(res, body) : undefined;
  return {
    ok: false,
    status: res.status,
    error: body?.error ?? (res.status === 429 ? TOO_MANY_ATTEMPTS_MESSAGE : "Something went wrong. Please try again."),
    ...(body?.code ? { code: body.code } : {}),
    ...(wait ? { retryAfterSeconds: wait } : {}),
  };
}

export async function getLinkedCustomerToken(
  storage?: TokenStorage | null
): Promise<string | null> {
  const token = await getToken(storage);
  if (!token) return null;
  try {
    const data = await fetchApi<{ token: string }>("/api/auth/link/customer-token", { token });
    return data?.token ?? null;
  } catch {
    return null;
  }
}

export async function getLinkedBusinessToken(
  storage?: TokenStorage | null
): Promise<string | null> {
  const token = await getCustomerToken(storage);
  if (!token) return null;
  try {
    const data = await fetchApi<{ token: string }>("/api/auth/link/business-token", { token });
    return data?.token ?? null;
  } catch {
    return null;
  }
}

/**
 * Update the signed in owner's business settings (PUT /api/settings), e.g.
 * `{ address, hideFullAddress: true }`. Returns the updated business.
 */
export async function updateBusinessSettings<T = Record<string, unknown>>(
  payload: UpdateBusinessSettingsPayload,
  storage?: TokenStorage | null
): Promise<T> {
  const token = await getToken(storage);
  if (!token) throw new Error("Not signed in");
  return fetchApi<T>("/api/settings", { method: "PUT", body: JSON.stringify(payload), token });
}
